import { useIsFocused } from '@react-navigation/native';
import { router } from 'expo-router';
import { useEffect } from 'react';

import { routes } from '@/constants/routes';
import { useCustomerQuery } from '@/hooks/queries/customer/useCustomerQuery';
import { useStandbyActiveEntryQuery } from '@/hooks/queries/standby/useStandbyActiveEntryQuery';
import { useStandbyPublicShopQuery } from '@/hooks/queries/standby/useStandbyPublicShopQuery';
import {
  isStandbyExitActive,
  standbyExitClearsInMs,
} from '@/utils/standby/standbyExitIntent';

import type {
  StandbyActiveEntryGuardOptions,
  StandbyActiveEntryGuardResult,
} from './types';

/**
 * A signed-in customer with an active standby entry at this shop today must
 * never see or act on stylist/service/summary — join is a one-per-day gate
 * server-side, so letting them re-select would only dead-end at the 409
 * they'd hit on submit. Shared across every screen after registration's
 * handoff (registration itself, stylist, service, summary) so the check and
 * the redirect live in exactly one place.
 *
 * Fails OPEN on a lookup error: a transient failure here should not stand
 * between an otherwise-fine customer and the funnel — the join-time 409
 * redirect (Summary screen) is the backstop this is speeding past, not
 * replacing.
 */
export const useStandbyActiveEntryGuard = (
  shopId: string,
  options?: StandbyActiveEntryGuardOptions,
): StandbyActiveEntryGuardResult => {
  const suppressRedirect = options?.suppressRedirect ?? false;
  const { customerId } = useCustomerQuery();
  const isSignedIn = !!customerId;
  const isFocused = useIsFocused();

  // The join/active-entry endpoints are UUID-only (squire-api rejects a
  // shop slug/route with a 404), but `shopId` here is the funnel's route
  // param, which may be a shareable slug. Resolve the canonical shop id off
  // the already-cached public-shop read (every funnel screen loads it) and
  // key the lookup on that; navigation below keeps the original route param.
  const { data: shopData, isError: isShopError } =
    useStandbyPublicShopQuery(shopId);
  const resolvedShopId = shopData?.shop.id;

  const { data, isLoading, isError } = useStandbyActiveEntryQuery(
    resolvedShopId ?? '',
    customerId,
    { enabled: isSignedIn && !!resolvedShopId },
  );

  // Settled once there is nothing left to wait on: not signed in, the shop id
  // couldn't be resolved (fail open — the screen's own shop query surfaces
  // that error), the lookup itself errored (fail open), or the lookup's
  // INITIAL load is done. Keying off the initial load (`isLoading`) rather
  // than `isFetching` means a focus/background refetch of already-cached data
  // keeps the current UI instead of flashing the guard's spinner on every
  // window focus.
  const isSettled =
    !isSignedIn || isShopError || isError || (!!resolvedShopId && !isLoading);
  const activeEntryId = isSettled && !isError ? (data?.entryId ?? null) : null;

  useEffect(() => {
    // Only the focused screen may act on the answer. After a successful join
    // the hook writes the active-entry cache, which notifies every mounted
    // guard (the stylist/service screens still under Summary in the stack) —
    // without this gate they would all fire competing redirects, and one
    // without the post-join `joined` flag could beat the summary's own.
    if (!activeEntryId || suppressRedirect || !isFocused) return;

    // The exit-intent latch covers instances `suppressRedirect` cannot reach:
    // during a back-out transition the OUTGOING screen's guard is still
    // mounted, sees the active entry, and would race the exit confirmation
    // off the screen — see standbyExitIntent.ts.
    if (!isStandbyExitActive(shopId)) {
      router.replace(routes.standbyEntry(shopId, activeEntryId));
      return;
    }

    // Nothing else about this effect's own dependencies necessarily changes
    // when the latch clears, so without this the redirect above would never
    // fire again for an instance that first evaluated mid-latch — schedule a
    // re-check for when it does instead of relying on an unrelated rerun.
    const timer = setTimeout(() => {
      if (isFocused && !isStandbyExitActive(shopId)) {
        router.replace(routes.standbyEntry(shopId, activeEntryId));
      }
    }, standbyExitClearsInMs(shopId));

    return () => clearTimeout(timer);
  }, [activeEntryId, shopId, suppressRedirect, isFocused]);

  return {
    isChecking:
      !isSettled ||
      (!!activeEntryId &&
        !suppressRedirect &&
        isFocused &&
        !isStandbyExitActive(shopId)),
    hasActiveEntry: !!activeEntryId,
    activeEntryId,
  };
};
